// 式神の進化の演出。
// prefers-reduced-motion なら結果だけを短く。演出の失敗で操作を止めない。
import { shikiFormName } from '@hyakki/core';
import { sfx } from '../audio';
import { shikiArtHtml } from '../assets';
import { esc } from './dom';

const reduced = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;


/** 進化の演出の素材（art-src/gen-evolve-fx.sh：Codex 生成、黒地をスクリーン合成で抜く） */
const EVO = '/art/fx/evolve/';
/** 連続コマ（4×4）の各コマを出す時刻（ms）。1〜7 札が集まり陣が開く、8〜10 光の柱と繭、11 閃光、12〜16 衝撃の輪と花びら */
const SEQ_AT = [0, 170, 340, 510, 700, 880, 1060, 1260, 1460, 1660, 1880, 2020, 2220, 2440, 2680, 2950];
/** 繭が弾けて新しい姿になる時刻（11コマ目） */
const POP_AT = 1880;
const END_AT = 3300;

/**
 * 進化の演出（全画面）：Codex に描かせた連続コマ（札が渦を巻いて集まる → 足元に陣 → 光の柱と繭 → 閃光 → 衝撃の輪と花びら）を
 * 式神の絵に重ねて再生する（足元に陣を残すのはやめた）。★が1つずつ灯り「進化」と名前。
 * 1回押すと最後の場面まで飛ばし、もう1回で閉じる。動きを減らす設定なら最後の場面だけ。
 */
export function playEvolve(enemyId: string, from: number, to: number): Promise<void> {
  return new Promise<void>((resolve) => {
    try {
      const fromName = shikiFormName(enemyId, from);
      const toName = shikiFormName(enemyId, to);
      const el = document.createElement('div');
      el.className = `evofx s${to} ${reduced() ? 'reduced end' : ''}`;
      el.setAttribute('role', 'status');
      el.setAttribute('aria-label', `${fromName}が${toName}に進化`);
      el.style.setProperty('--pop', `${POP_AT}ms`);
      el.innerHTML = `<div class="evofx-stage">
          <span class="evofx-old">${shikiArtHtml(enemyId, 220, from)}</span>
          <span class="evofx-new">${shikiArtHtml(enemyId, 260, to)}</span>
          <i class="evofx-seq" style="background-image:url(${EVO}seq.webp)"></i>
        </div>
        <div class="evofx-flash"></div>
        <div class="evofx-text">
          <b class="evofx-title">進化</b>
          <p class="evofx-stars">${Array.from({ length: to }, (_, i) => `<i style="--d:${POP_AT + 500 + i * 160}ms">★</i>`).join('')}</p>
          <p class="evofx-names"><small>${esc(fromName)}</small><span>➜</span><strong>${esc(toName)}</strong></p>
          <small class="evofx-skip">押すと閉じる</small>
        </div>`;
      document.body.appendChild(el);
      const seq = el.querySelector<HTMLElement>('.evofx-seq')!;
      const show = (n: number) => seq.style.setProperty('background-position', `${(n % 4) * 33.333}% ${Math.floor(n / 4) * 33.333}%`);
      show(0);
      const timers: number[] = [];
      if (!reduced()) {
        SEQ_AT.forEach((at, n) => timers.push(window.setTimeout(() => show(n), at)));
        timers.push(window.setTimeout(() => seq.classList.add('gone'), SEQ_AT[15] + 350));
        timers.push(window.setTimeout(() => { try { sfx('ougi'); } catch { /* 音が無くても進める */ } }, POP_AT - 60));
      }
      try {
        sfx('enhanceBig');
      } catch {
        /* 音が無くても進める */
      }
      let done = false;
      const close = () => {
        if (done) return;
        done = true;
        timers.forEach(clearTimeout);
        el.classList.add('out');
        setTimeout(() => el.remove(), 220);
        resolve();
      };
      const toEnd = () => {
        timers.forEach(clearTimeout);
        el.classList.add('end');
      };
      timers.push(window.setTimeout(toEnd, END_AT));
      el.addEventListener('pointerdown', () => (el.classList.contains('end') ? close() : toEnd()));
    } catch {
      resolve();
    }
  });
}
